/**
 * Organizations API Service
 *
 * Handles organization CRUD, member management, and invitations.
 */

import { API_BASE, getAuthHeaders, handleResponse } from './apiClient';

// =============================================================================
// Organization CRUD
// =============================================================================

/**
 * Create a new organization
 */
export const createOrganization = async (user, data) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * List user's organizations
 */
export const listOrganizations = async (user) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Get organization details
 */
export const getOrganization = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Update organization
 */
export const updateOrganization = async (user, orgId, data) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * Delete organization
 */
export const deleteOrganization = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * Get or create personal organization
 */
export const getOrCreatePersonalOrg = async (user) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/personal`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

// =============================================================================
// Member Management
// =============================================================================

/**
 * List organization members
 */
export const listMembers = async (user, orgId, params = {}) => {
  const headers = await getAuthHeaders(user);
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);

  const url = `${API_BASE}/organizations/${orgId}/members${queryParams.toString() ? '?' + queryParams : ''}`;
  const response = await fetch(url, { headers });
  return handleResponse(response);
};

/**
 * Get member details
 */
export const getMember = async (user, orgId, memberUid) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/members/${memberUid}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Update member role
 */
export const updateMemberRole = async (user, orgId, memberUid, role) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/members/${memberUid}/role`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ role }),
  });
  return handleResponse(response);
};

/**
 * Remove member from organization
 */
export const removeMember = async (user, orgId, memberUid) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/members/${memberUid}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * Leave organization
 */
export const leaveOrganization = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/leave`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

/**
 * Transfer ownership
 */
export const transferOwnership = async (user, orgId, newOwnerUid) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/transfer`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ new_owner_uid: newOwnerUid }),
  });
  return handleResponse(response);
};

// =============================================================================
// Invitations
// =============================================================================

/**
 * Create invitation
 */
export const createInvite = async (user, orgId, email, role) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invites`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, role }),
  });
  return handleResponse(response);
};

/**
 * List pending invites
 */
export const listInvites = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invites`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Cancel invite
 */
export const cancelInvite = async (user, orgId, inviteId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invites/${inviteId}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * Get invite details (no auth required)
 */
export const getInviteDetails = async (token) => {
  const response = await fetch(`${API_BASE}/organizations/invites/${token}`);
  return handleResponse(response);
};

/**
 * Accept invite
 */
export const acceptInvite = async (user, token) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/invites/${token}/accept`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

// =============================================================================
// Audit Log
// =============================================================================

/**
 * Get organization audit log
 */
export const getAuditLog = async (user, orgId, params = {}) => {
  const headers = await getAuthHeaders(user);
  const queryParams = new URLSearchParams();

  if (params.action) queryParams.append('action', params.action);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);

  const url = `${API_BASE}/organizations/${orgId}/audit${queryParams.toString() ? '?' + queryParams : ''}`;
  const response = await fetch(url, { headers });
  return handleResponse(response);
};

// =============================================================================
// Activity Feed
// =============================================================================

/**
 * Get organization activity feed
 */
export const getActivityFeed = async (user, orgId, params = {}) => {
  const headers = await getAuthHeaders(user);
  const queryParams = new URLSearchParams();

  if (params.types) queryParams.append('types', params.types);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);

  const url = `${API_BASE}/organizations/${orgId}/activity${queryParams.toString() ? '?' + queryParams : ''}`;
  const response = await fetch(url, { headers });
  return handleResponse(response);
};

// =============================================================================
// Invite Links (Shareable URLs)
// =============================================================================

/**
 * Create a shareable invite link
 */
export const createInviteLink = async (user, orgId, data) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invite-links`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * List active invite links
 */
export const listInviteLinks = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invite-links`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Revoke an invite link
 */
export const revokeInviteLink = async (user, orgId, linkId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/${orgId}/invite-links/${linkId}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
};

/**
 * Get invite link details (no auth required)
 */
export const getInviteLinkDetails = async (token) => {
  const response = await fetch(`${API_BASE}/organizations/invite-links/${token}`);
  return handleResponse(response);
};

/**
 * Accept an invite link
 */
export const acceptInviteLink = async (user, token) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/organizations/invite-links/${token}/accept`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
};

// =============================================================================
// Export
// =============================================================================

const organizationsApi = {
  // Organization CRUD
  createOrganization,
  listOrganizations,
  getOrganization,
  updateOrganization,
  deleteOrganization,
  getOrCreatePersonalOrg,

  // Members
  listMembers,
  getMember,
  updateMemberRole,
  removeMember,
  leaveOrganization,
  transferOwnership,

  // Invites (Email)
  createInvite,
  listInvites,
  cancelInvite,
  getInviteDetails,
  acceptInvite,

  // Invite Links (Shareable URLs)
  createInviteLink,
  listInviteLinks,
  revokeInviteLink,
  getInviteLinkDetails,
  acceptInviteLink,

  // Activity Feed
  getActivityFeed,

  // Audit
  getAuditLog,
};

export default organizationsApi;
